/**
 * The four pages every member has, plus the profile.
 *
 * They all read the same `Me` the shell already fetched, so a licence's state
 * is one fact on four pages rather than four requests that can disagree. What
 * they do *not* share is the writing: each action posts, then asks the shell to
 * refresh, because the answer to "did that work?" is the server's to give.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import { color, font, radius, type as t } from '../theme/tokens';
import {
  Badge,
  Callout,
  Card,
  Empty,
  Field,
  GhostButton,
  Input,
  Kicker,
  PrimaryButton,
  Row,
  RowAction,
  StatStrip,
  StatusCell,
  Table
} from '../components/primitives';
import { administered, TeamPlan } from './Organizations';
import { TwoFactorCard } from './identity/TwoFactorProfile';
import { api, ApiError, apiPath } from '../lib/api';
import { useConfirm } from '../components/Modal';
import { ACCESS_LABEL, isoDay, money, monthYear } from '../lib/format';
import { OTHER_SYSTEMS, platform } from '../lib/platform';
import type { Device, DeviceTokenRow, InvoiceRow, Me } from '../lib/types';
import { DeviceTokens } from './cloud/DeviceTokens';
import { OpenInAirlock, canOpenInAirlock } from './cloud/OpenInAirlock';

/**
 * How long the app runs before it asks for a key. The gate is what enforces it
 * (`auth/src/trial.ts`) and this is only what the pages say out loud — the same
 * arrangement the desktop app's licence wall has.
 */
const TRIAL_DAYS = 14;

/* ── dashboard ──────────────────────────────────────────────────────────── */

export function Dashboard({
  me,
  onGo,
  onRefresh
}: {
  me: Me;
  onGo: (page: 'licence' | 'downloads') => void;
  onRefresh: () => Promise<void>;
}): React.JSX.Element {
  const { license, access } = me;

  if (!license) {
    return (
      <>
        {canOpenInAirlock(me) && <OpenInAirlock onDownloads={() => onGo('downloads')} />}
        <SeatNote me={me} />
        <Empty title="No licence on this account yet">
          A licence is $5 a month or $50 a year, excluding VAT — one key, and it never changes. You do not need one to
          start: the app runs for {TRIAL_DAYS} days on this computer, with nothing to enter and nothing to cancel.
          <div style={{ marginTop: 18, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <PrimaryButton onClick={() => onGo('licence')}>Choose a plan</PrimaryButton>
            <GhostButton onClick={() => onGo('downloads')}>Download the {TRIAL_DAYS}-day trial</GhostButton>
          </div>
        </Empty>
        <div style={{ marginTop: 20 }}>
          <StatStrip
            items={[
              { label: 'Serial', value: '—' },
              { label: 'Member since', value: monthYear(me.user.createdAt) },
              { label: 'Invoices', value: '0' },
              { label: 'Organizations', value: String(me.organizations.length) }
            ]}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {canOpenInAirlock(me) && <OpenInAirlock onDownloads={() => onGo('downloads')} />}
      <SeatNote me={me} />
      {/* The key's tile across the column, then the subscription and the build
          side by side at one height (redesign §39.1, §39.2). */}
      <KeyCard licenseKey={license.key} quiet={canOpenInAirlock(me) || access === 'lapsed'} />
      <div
        className="al-dash"
        style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', alignItems: 'stretch', gap: 20, marginTop: 20 }}
      >
        <SubscriptionCard me={me} onGo={onGo} onRefresh={onRefresh} />
        <BuildCard onGo={onGo} licensed={access === 'active' || access === 'grace'} />
      </div>
      <div style={{ marginTop: 20 }}>
        <StatStrip
          items={[
            { label: 'Serial', value: `#${license.serial}` },
            { label: 'Member since', value: monthYear(me.user.createdAt) },
            { label: 'Access', value: ACCESS_LABEL[access] ?? access },
            { label: 'Organizations', value: String(me.organizations.length) }
          ]}
        />
      </div>
    </>
  );
}

function KeyCard({ licenseKey, quiet = false }: { licenseKey: string | null; quiet?: boolean }): React.JSX.Element {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    if (!licenseKey) return;
    await navigator.clipboard.writeText(licenseKey);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  // The page's one tile (redesign §39.1): the site's #F5F5F7 instead of the
  // dark card, the key large in mono, Copy key on its right.
  return (
    <div className="al-key-tile" style={{ background: color.surfaceAlt, borderRadius: radius.xxl, padding: '28px 32px' }}>
      <Kicker>Your licence key</Kicker>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
        <output
          aria-label={licenseKey ? `Licence key ${licenseKey}` : 'Licence key'}
          className="al-licence-key"
          style={{
            flex: '1 1 320px',
            minWidth: 0,
            fontFamily: font.mono,
            fontSize: 24,
            lineHeight: 1.3,
            letterSpacing: '.06em',
            fontVariantNumeric: 'tabular-nums',
            color: color.ink,
            userSelect: 'all',
            overflowWrap: 'anywhere'
          }}
        >
          {licenseKey ?? 'Waiting for the gate to mint it — reload in a moment.'}
        </output>
        {/* One primary per view: a more urgent verb on the page takes it (§39.1). */}
        {licenseKey &&
          (quiet ? (
            <GhostButton size="sm" onClick={() => void copy()}>
              <span aria-live="polite">{copied ? 'Copied' : 'Copy key'}</span>
            </GhostButton>
          ) : (
            <PrimaryButton size="sm" onClick={() => void copy()}>
              <span aria-live="polite">{copied ? 'Copied' : 'Copy key'}</span>
            </PrimaryButton>
          ))}
      </div>
      <p style={{ fontSize: 13, lineHeight: 1.6, color: color.inkMuted, margin: '14px 0 0', maxWidth: 560 }}>
        This key never changes. It stays the same through every renewal — it is the credential the app updates with, so
        keep it.
      </p>
    </div>
  );
}

function SubscriptionCard({
  me,
  onGo,
  onRefresh
}: {
  me: Me;
  onGo: (page: 'licence') => void;
  onRefresh: () => Promise<void>;
}): React.JSX.Element {
  const { license, access } = me;
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [dialog, ask] = useConfirm();

  const dotColour = access === 'active' ? color.accent : access === 'grace' ? color.yellow : color.red;

  const cancel = async (): Promise<void> => {
    const agreed = await ask({
      title: 'Cancel at the end of the paid period?',
      lede: 'Nothing stops today: access runs to what you have already paid for, and the key keeps working until then.',
      confirm: 'Cancel the renewal',
      danger: true
    });
    if (!agreed) return;
    setBusy(true);
    try {
      await api.post('/billing/cancel');
      await onRefresh();
      setNote('Cancelled. Access runs to the end of the paid period.');
    } catch (failure) {
      setNote(failure instanceof ApiError ? failure.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  // Resume takes a cancellation back while the paid period still runs.
  const resume = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.post('/billing/resume');
      await onRefresh();
      setNote('Resumed. The licence renews at the end of the period.');
    } catch (failure) {
      setNote(failure instanceof ApiError ? failure.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  // A seat's licence is the organization's to buy and to stop: no plan, no
  // Cancel, only where it comes from (redesign, 42a / 42c).
  if (license?.orgId) {
    const seatOrg = me.organizations.find((organization) => organization.id === license.orgId);
    return (
      <Card padding={22}>
        <Kicker>Seat</Kicker>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 12 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColour, display: 'block' }} />
          <span style={{ fontSize: 18, fontWeight: 600 }}>{seatOrg ? `Seat in ${seatOrg.name}` : 'Seat held'}</span>
        </div>
        <div style={{ fontSize: 14, color: color.inkSubtle, marginTop: 10, lineHeight: 1.6 }}>
          {access === 'lapsed' || access === 'revoked'
            ? 'This seat no longer opens Airlock.'
            : `${seatOrg?.name ?? 'The organization'} holds the subscription; its owner or an administrator answers for the seat.`}
        </div>
      </Card>
    );
  }

  // A beta bought nothing and renews nothing, and a lapsed licence has ended:
  // neither is a "Personal · monthly" subscription that "then renews".
  const beta = license?.plan === 'beta';
  const lapsed = access === 'lapsed';
  const title = beta ? 'Beta' : `Personal · ${license?.plan === 'annual' ? 'yearly' : 'monthly'}`;
  const renewing = !beta && !lapsed && access !== 'revoked';

  return (
    <Card padding={22}>
      {dialog}
      <Kicker>{beta ? 'Licence' : 'Subscription'}</Kicker>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 12 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColour, display: 'block' }} />
        <span style={{ fontSize: 18, fontWeight: 600 }}>{title}</span>
      </div>
      <div style={{ fontSize: 14, color: color.inkSubtle, marginTop: 10, lineHeight: 1.6 }}>
        {access === 'revoked' ? (
          'This licence is revoked.'
        ) : lapsed ? (
          <>
            Access ended on <strong>{isoDay(license?.expiresAt ?? null)}</strong>.
          </>
        ) : (
          <>
            Access runs to <strong>{isoDay(license?.expiresAt ?? null)}</strong>
            {beta || license?.cancelAtPeriodEnd ? ', and does not renew.' : ', then renews.'}
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
        <PrimaryButton size="sm" onClick={() => onGo('licence')}>
          {beta || lapsed ? 'Choose a plan' : 'Change plan'}
        </PrimaryButton>
        {renewing && !license?.cancelAtPeriodEnd && (
          <GhostButton size="sm" onClick={() => void cancel()}>
            {busy ? 'Cancelling' : 'Cancel'}
          </GhostButton>
        )}
        {renewing && license?.cancelAtPeriodEnd && (
          <GhostButton size="sm" onClick={() => void resume()}>
            {busy ? 'Resuming' : 'Resume'}
          </GhostButton>
        )}
      </div>
      {note && <p style={{ fontSize: 13, color: color.inkSubtle, margin: '12px 0 0' }}>{note}</p>}
    </Card>
  );
}

/**
 * What somebody in another person's organization is told, and the whole of it.
 *
 * They are in a team and it would be strange to pretend otherwise — but seats,
 * invites and billing belong to whoever owns it, so this is a sentence rather
 * than a link. Nothing here leads anywhere they would be refused.
 *
 * Being a member and holding a seat are said separately, because they are now
 * two facts: somebody invited last week is here, in their teams, and cannot
 * open Airlock until an administrator gives them one. Telling them they hold a
 * seat would send them to look for a key that has not been minted.
 */
function SeatNote({ me }: { me: Me }): React.JSX.Element | null {
  const seats = me.organizations.filter((organization) => organization.role === 'member');
  if (seats.length === 0) return null;
  const names = seats.map((organization) => organization.name).join(', ');
  const waiting = seats.filter((organization) => organization.seatStatus === 'member');
  return (
    <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '0 0 20px', lineHeight: 1.6 }}>
      {waiting.length === seats.length
        ? `You are a member of ${names}, and no seat has been given to you yet — Airlock unlocks on your machines as soon as one is. `
        : `You hold a seat in ${names}. `}
      Seats, invites and billing are the owner’s; your licence, downloads and profile are yours and are all here.
    </p>
  );
}

function BuildCard({ onGo, licensed }: { onGo: (page: 'downloads') => void; licensed: boolean }): React.JSX.Element {
  const [build, setBuild] = useState<{ version: string; releasedAt: string | null } | null>(null);

  useEffect(() => {
    void api
      .get<{ latest: { version: string; releasedAt: string | null } | null }>('/builds')
      .then((answer) => setBuild(answer.latest))
      .catch(() => setBuild(null));
  }, []);

  return (
    <Card padding={22}>
      <Kicker>Latest build</Kicker>
      <div style={{ fontSize: 18, fontWeight: 600, marginTop: 12 }}>
        {build ? `Airlock ${build.version}` : 'Ask the gate…'}
      </div>
      <div style={{ fontSize: 13.5, color: color.inkSubtle, marginTop: 6 }}>
        {build?.releasedAt ? `Released ${isoDay(build.releasedAt)} · macOS, Apple silicon` : 'macOS, Apple silicon'}
      </div>
      <div style={{ marginTop: 16 }}>
        <GhostButton full onClick={() => onGo('downloads')}>
          {licensed ? 'Choose a download' : `Download the ${TRIAL_DAYS}-day trial`}
        </GhostButton>
      </div>
    </Card>
  );
}

/* ── licence & plan ─────────────────────────────────────────────────────── */

/**
 * Licence & plan, which is a different page depending on who is holding an
 * account — and used to be one page showing the personal licence to everybody.
 *
 * An organization's owner was being offered a *personal* subscription here,
 * beside an organization whose seats they could not buy anywhere. Three cases
 * now, and each is somebody's whole answer:
 *
 *   administers an organization  its seats, priced per seat, bought once and
 *                                prorated after (`TeamPlan`)
 *   holds somebody else's seat   nothing to buy — the seat is the licence, and
 *                                who to ask is the useful sentence
 *   personal                     the two personal plans, as before
 */
export function LicencePlan({ me }: { me: Me }): React.JSX.Element {
  const organization = administered(me);
  if (organization) return <TeamPlan me={me} organization={organization} />;
  if (me.organizations.length > 0) return <SeatHolderPlan me={me} />;
  // A licence that was given rather than bought says so. Not decoration: it is
  // what stops a tester looking for an invoice that does not exist, and what
  // tells them the date before it arrives.
  if (me.license?.plan === 'beta' && me.access !== 'revoked') return <BetaPlan me={me} />;
  return <PersonalPlan me={me} />;
}

/**
 * A beta licence: no invoice, no plan to change, and a date.
 *
 * The plans are still offered underneath, because a tester who wants to keep
 * Airlock should not have to wait for their access to lapse first — buying
 * retires the grant in the same breath.
 */
function BetaPlan({ me }: { me: Me }): React.JSX.Element {
  const until = me.license?.paidThrough ?? null;
  const lapsing = me.access === 'grace';
  return (
    <>
      <Card style={{ maxWidth: 620 }} accent={lapsing}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>
          Beta{until ? ` — runs to ${isoDay(until)}` : ''}
        </div>
        <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '8px 0 0', lineHeight: 1.6 }}>
          {lapsing
            ? 'Your beta has ended and you are in the grace week: Airlock still runs, and stops when it is over.'
            : 'A licence we gave you rather than sold you. There is no subscription behind it and nothing to cancel, so there are no invoices on this account.'}
        </p>
        <p style={{ fontSize: 13, color: color.inkFaint, margin: '14px 0 0', lineHeight: 1.6 }}>
          Your key is on the Overview, and it does not change when the date does — if the beta is extended, nothing on
          your machines has to be touched.
        </p>
      </Card>
      <div style={{ marginTop: 20 }}>
        <PersonalPlan me={me} />
      </div>
    </>
  );
}

/**
 * Somebody who holds a seat in an organization they do not administer.
 *
 * There is nothing on this page for them to buy, and the honest version of that
 * is a sentence naming who can — not a pair of personal plans that would sell
 * them a second licence for a machine already covered by their seat.
 */
function SeatHolderPlan({ me }: { me: Me }): React.JSX.Element {
  const organization = me.organizations[0];
  // A member is not yet a seat holder: the seat's text would send them to look
  // for a key that has not been minted (redesign, 47b).
  if (organization.seatStatus === 'member') {
    return (
      <Card style={{ maxWidth: 620 }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>No seat yet in {organization.name}</div>
        <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '8px 0 0', lineHeight: 1.6 }}>
          This account belongs to {organization.name}: its licence is a seat, given by the owner or an administrator.
        </p>
      </Card>
    );
  }
  return (
    <Card style={{ maxWidth: 620 }}>
      <div style={{ fontSize: 16, fontWeight: 600 }}>Your seat is {organization.name}’s</div>
      <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '8px 0 0', lineHeight: 1.6 }}>
        The organization holds the subscription and one invoice for all of it, so there is nothing to buy here. Your key
        and the machines it is installed on are on the Overview, and the owner or an administrator of{' '}
        {organization.name} is who to ask about the seat itself.
      </p>
    </Card>
  );
}

function PersonalPlan({ me }: { me: Me }): React.JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);
  const [period, setPeriod] = useState('annual');
  const [error, setError] = useState<string | null>(null);
  /**
   * The plan this account *bought*, which is not the same question as which
   * licence it holds. A granted beta carries a plan nobody sold, has no Stripe
   * customer behind it, and so has nothing to switch and no portal to open —
   * reading it as a subscription offered a tester a prorated upgrade from a
   * licence that was free, and a billing portal that answers 404.
   */
  const bought = me.license && me.license.plan !== 'beta' ? me.license : null;
  const current = bought?.plan;

  /**
   * Buying and changing are different verbs. A first licence goes through
   * Checkout; a change of period is Stripe's own portal, because proration,
   * tax and the card on file are all decisions this Worker should not be
   * re-implementing.
   */
  const start = async (plan: string): Promise<void> => {
    setBusy(plan);
    setError(null);
    try {
      const answer = await api.post<{ url: string }>('/checkout', { plan });
      window.location.href = answer.url;
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
      setBusy(null);
    }
  };

  const openPortal = async (): Promise<void> => {
    setBusy('portal');
    setError(null);
    try {
      const answer = await api.post<{ url: string }>('/billing/portal');
      window.location.href = answer.url;
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
      setBusy(null);
    }
  };

  /**
   * A licence that was bought is one card and one button.
   *
   * The price list stayed on screen after the sale as the way to switch period,
   * and its button went to Stripe's portal — the same portal the "Billing
   * details" card underneath opened, with a paragraph explaining where company
   * name and VAT number live. Three things to read and two buttons, for one
   * destination. Somebody who has already paid opens this page to change
   * something, and everything there is to change is behind that one door:
   * period, card, address, and cancelling.
   *
   * `plans` is the deployment's price list and a bought plan is normally in it;
   * a plan it has no entry for still names itself, rather than rendering a card
   * with a blank where the price goes.
   */
  const offer = me.config.plans.find((one) => one.id === period) ?? me.config.plans[0];

  if (bought) {
    const plan = me.config.plans.find((one) => one.id === current);
    return (
      <>
        <Card style={{ maxWidth: 560, border: `2px solid ${color.accentText}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18, fontWeight: 600 }}>{plan?.label ?? current}</span>
            <Badge tone="ok">Current</Badge>
          </div>
          {plan && (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 18 }}>
                <span style={{ fontSize: t.priceApp, letterSpacing: '-.03em', fontWeight: 600 }}>${plan.priceUsd}</span>
                <span style={{ fontSize: 14, color: color.inkSubtle }}>{plan.period} excl. VAT</span>
              </div>
              <div style={{ fontSize: 13.5, color: color.inkSubtle, marginTop: 8 }}>
                {plan.id === 'annual'
                  ? `$${(plan.priceUsd / 12).toFixed(2)} a month, billed once.`
                  : 'Billed monthly, cancel any time.'}
              </div>
            </>
          )}
        </Card>
        <div style={{ marginTop: 20 }}>
          <GhostButton onClick={() => void openPortal()}>
            {busy === 'portal' ? 'Opening' : 'Change the subscription'}
          </GhostButton>
        </div>
        {error && <p style={{ fontSize: 13.5, color: color.badText, marginTop: 16 }}>{error}</p>}
      </>
    );
  }

  return (
    <>
      {/* One card that changes in place under a Monthly / Yearly switch, as on
          the site's Pricing page (redesign §39.3). Nothing is bought here — that
          case returned above — so the card is for sale. */}
      <PeriodSwitch value={period} onChange={setPeriod} />
      {offer && (
        <div style={{ maxWidth: 560, marginTop: 16, border: `1px solid ${color.line}`, borderRadius: radius.xxl }}>
          <div style={{ padding: 24 }}>
            <div style={{ fontSize: 17, fontWeight: 600 }}>{offer.label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 14 }}>
              <span style={{ fontSize: 40, lineHeight: 1, letterSpacing: '-.03em', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                ${offer.priceUsd}
              </span>
              <span style={{ fontSize: 14.5, color: color.inkMuted }}>{offer.period} excl. VAT</span>
            </div>
            <div style={{ fontSize: 13, color: color.inkSubtle, marginTop: 10 }}>
              {offer.id === 'annual'
                ? `$${(offer.priceUsd / 12).toFixed(2)} a month, billed once.`
                : 'Billed monthly, cancel any time.'}
            </div>
            {!offer.onSale && (
              <div style={{ fontSize: 13, color: color.warnText, marginTop: 10 }}>
                Not on sale on this deployment — no Stripe price is configured.
              </div>
            )}
          </div>
          {offer.onSale && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '14px 24px', borderTop: `1px solid ${color.line}` }}>
              <PrimaryButton size="sm" onClick={() => void start(offer.id)}>
                {busy === offer.id ? 'Buying' : `Buy ${offer.id === 'annual' ? 'yearly' : 'monthly'}`}
              </PrimaryButton>
            </div>
          )}
        </div>
      )}

      {/* No billing card here. Company name, VAT number and address are Stripe's
          and are edited in its portal, and an account that has bought nothing has
          no customer there to open one for. */}
      {error && <p style={{ fontSize: 13.5, color: color.badText, marginTop: 16 }}>{error}</p>}
    </>
  );
}

/* ── billing history ────────────────────────────────────────────────────── */

/**
 * The invoice table. `path` is whose: the account's own (`/invoices`) by
 * default, or an organization's (`/orgs/:id/invoices`) on its Billing page —
 * one table for both, so the two can never draw the same invoice differently.
 */
export function BillingHistory({ path = '/invoices' }: { path?: string } = {}): React.JSX.Element {
  const [invoices, setInvoices] = useState<InvoiceRow[] | null>(null);
  const columns = '118px 1fr 190px 130px 120px 90px';

  useEffect(() => {
    void api
      .get<{ invoices: InvoiceRow[] }>(path)
      .then((answer) => setInvoices(answer.invoices))
      .catch(() => setInvoices([]));
  }, [path]);

  if (invoices === null) return <Empty title="Reading your invoices…" />;
  if (invoices.length === 0) {
    return <Empty title="No invoices yet">The first one is issued when a subscription starts.</Empty>;
  }

  return (
    <>
      <Table columns={columns} header={['Date', 'Invoice', 'Period', 'Amount', 'Status', '']}>
        {invoices.map((invoice) => (
          <Row key={invoice.id} columns={columns}>
            <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkSubtle }}>
              {isoDay(invoice.periodStart)}
            </span>
            <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{invoice.number ?? invoice.id}</span>
            <span style={{ color: color.inkSubtle }}>
              {monthYear(invoice.periodStart)} — {monthYear(invoice.periodEnd)}
            </span>
            <span>{money(invoice.amountCents, invoice.currency)} + VAT</span>
            <StatusCell
              tone={invoice.status === 'paid' ? 'ok' : invoice.status === 'open' ? 'warn' : 'off'}
              word={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
            />
            {invoice.pdfUrl ? (
              <a href={invoice.pdfUrl} style={{ fontSize: 13 }} target="_blank" rel="noreferrer">
                PDF
              </a>
            ) : (
              <span style={{ fontSize: 13, color: color.inkGhost }}>—</span>
            )}
          </Row>
        ))}
      </Table>
      <p style={{ fontSize: 13, color: color.inkFaint, margin: '16px 0 0', lineHeight: 1.6 }}>
        Payments are processed by Stripe. No card data touches Airlock’s servers; invoices are issued with French VAT
        rules applied and are kept for ten years, as accounting law requires.
      </p>
    </>
  );
}

/* ── downloads ──────────────────────────────────────────────────────────── */

/**
 * The one page that is never empty.
 *
 * A licence decides which build the gate streams — the keyed one, which keeps
 * updating — and not *whether* there is one to take: the fortnight is granted to
 * the machine by the app on first launch, so a download nobody could take
 * without a key would be a trial nobody could start. Both cases are the same
 * button, pointed at `/api/download`; what changes is the sentence beside it.
 *
 * One build is offered, not a table of them: the macOS one, the only one there
 * is since 2026-10-06.
 */
/** One build as `/api/builds` describes it. */
type Build = { version: string; releasedAt: string | null };

/**
 * The build, as a *description* the gate resolves — `os` — and never a file
 * name: the name carries the version, and which version is current is the
 * gate's to say.
 *
 * macOS on Apple silicon only, since 2026-10-06: the owner stopped the Windows
 * and Linux builds. The gate still serves their feeds so the installs already
 * out there keep working, but this page no longer offers them; somebody on
 * either system is told Airlock runs on macOS for now (`OTHER_SYSTEMS`).
 *
 * There is no first-launch callout: the build is signed and notarized since
 * 0.11.0, so it opens on a double-click.
 */
const DOWNLOAD = {
  system: 'macOS',
  detail: 'Apple silicon',
  note: 'Signed and notarized.',
  links: [{ label: 'Download .dmg', href: '/api/download?os=mac' }]
} as const;

export function Downloads({ me, onGo }: { me: Me; onGo: (page: 'licence') => void }): React.JSX.Element {
  const [build, setBuild] = useState<{ platforms: { mac?: Build | null }; licensed: boolean } | null>(null);
  // The system this browser is on, read once. Nothing here re-renders on a
  // different answer, because a machine does not change operating system while
  // somebody looks at the page.
  const [system] = useState(platform);

  useEffect(() => {
    void api
      .get<{ platforms?: { mac?: Build | null }; licensed: boolean }>('/builds')
      .then((answer) => setBuild({ platforms: answer.platforms ?? {}, licensed: answer.licensed }))
      .catch(() => setBuild({ platforms: {}, licensed: false }));
  }, []);

  if (!build) return <Empty title="Asking the gate what it is serving…" />;

  const download = DOWNLOAD;
  const latest = build.platforms.mac ?? null;

  return (
    <>
      <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '0 0 14px', lineHeight: 1.6, maxWidth: 700 }}>
        {build.licensed
          ? 'The gate checks your key before it streams a file, and an install keeps updating from the same key.'
          : `It runs for ${TRIAL_DAYS} days on the machine it is installed on — the app asks for the trial itself, and there is nothing to enter.`}
      </p>
      <div style={{ background: color.surface, border: `1px solid ${color.line}`, borderRadius: radius.xxl, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '18px 22px', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              Airlock {latest?.version ?? 'current'}{' '}
              <span style={{ fontWeight: 400, color: color.inkFaint }}>
                · {download.system} {download.detail}
              </span>
            </div>
            <div style={{ fontSize: 13, color: color.inkSubtle, marginTop: 4 }}>{download.note}</div>
            {system !== 'mac' && (
              <div style={{ fontSize: 13, color: color.inkSubtle, marginTop: 4 }}>{OTHER_SYSTEMS}</div>
            )}
          </div>
          <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkFaint }}>
            {latest?.releasedAt ? isoDay(latest.releasedAt) : ''}
          </span>
          {/* Plain links, not fetch: the gate answers with a redirect to a file,
              and letting the browser follow it is what makes Save As work. */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {download.links.map((link, position) =>
              position === 0 ? (
                <a
                  key={link.href}
                  className="btn-primary"
                  href={link.href}
                  style={{
                    fontFamily: 'inherit',
                    fontSize: 13,
                    fontWeight: 500,
                    // Ink, as every primary since the redesign: teal is never a fill.
                    color: '#FFFFFF',
                    background: color.ink,
                    padding: '9px 15px',
                    borderRadius: radius.chip,
                    textDecoration: 'none'
                  }}
                >
                  {link.label}
                </a>
              ) : (
                <GhostButton key={link.href} size="sm" href={link.href}>
                  {link.label}
                </GhostButton>
              )
            )}
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 700 }}>
        {!build.licensed && (
          <Callout title={me.license ? 'This account’s access has lapsed' : `After the ${TRIAL_DAYS} days`}>
            {me.license
              ? 'Updates and the keyed download stop while a subscription is not current. Renewing makes the same key work again — it never changes, so there is nothing to re-paste.'
              : 'The window locks and asks for a key. A licence unlocks it and keeps the install receiving updates — paste it in the app, or here on the next visit.'}
            <div style={{ marginTop: 14 }}>
              <PrimaryButton size="sm" onClick={() => onGo('licence')}>
                {me.license ? 'Renew the licence' : 'See the plans'}
              </PrimaryButton>
            </div>
          </Callout>
        )}
      </div>
    </>
  );
}

/* ── machines ───────────────────────────────────────────────────────────── */

/**
 * The machines a licence is installed on, and the one action that matters:
 * removing one.
 *
 * This page exists because of a refusal. A licence covers a fixed number of
 * machines, and the app on the machine that is turned away cannot fix it —
 * freeing a slot is a decision about a *different* computer, and it belongs
 * where somebody is signed in. So the wall in the app points here.
 *
 * It is deliberately plain about the rule that makes the cap tolerable: a
 * machine that has not checked in for a month frees its own slot. Somebody
 * looking at a list with an old laptop on it should not have to guess whether
 * removing it is necessary.
 */
/** `GET /api/devices`: the licence's machines, and the device tokens reading the organization's projects (§5.25). */
interface DevicesAnswer {
  licensed: boolean;
  devices: Device[];
  max: number;
  networks: number;
  tokens?: DeviceTokenRow[];
}

export function Devices({ onGo }: { onGo: (page: 'licence') => void }): React.JSX.Element {
  const [dialog, ask] = useConfirm();
  const [answer, setAnswer] = useState<DevicesAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const columns = '1.4fr 150px 150px 110px';

  const load = async (): Promise<void> => {
    setError(null);
    try {
      setAnswer(await api.get<DevicesAnswer>('/devices'));
    } catch (failure) {
      // Emphatically *not* an empty list. This used to answer a failed read with
      // `{ licensed: true, devices: [] }`, which drew the table, the stat strip
      // and "Nothing yet" — three free slots and no machines — with the refusal
      // printed underneath as if it were a footnote. The gate throws rather than
      // returning nothing for exactly this reason (`devicesOf`), and the page
      // has to keep that distinction rather than flatten it.
      setAnswer(null);
      setError(failure instanceof ApiError ? failure.message : 'The machines could not be read.');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  if (error && !answer) {
    return (
      <Empty title="The machines could not be read">
        {error}
        <div style={{ marginTop: 18 }}>
          <PrimaryButton onClick={() => void load()}>Try again</PrimaryButton>
        </div>
      </Empty>
    );
  }
  if (!answer) return <Empty title="Reading the machines…" />;

  if (!answer.licensed) {
    return (
      <>
        <Empty title="No licence on this account">
          Machines are counted against a licence. The trial is bound to the one machine it runs on, and nothing about it
          appears here.
          <div style={{ marginTop: 18 }}>
            <PrimaryButton onClick={() => onGo('licence')}>Choose a plan</PrimaryButton>
          </div>
        </Empty>
        <DeviceTokens tokens={answer.tokens ?? []} onChanged={() => void load()} onError={setError} />
      </>
    );
  }

  const remove = async (fingerprint: string, name: string): Promise<void> => {
    const agreed = await ask({
      title: `Remove ${name}?`,
      lede: 'Airlock stops working on it at its next check. The machine allowance comes back, so another can take its place.',
      confirm: 'Remove it',
      danger: true
    });
    if (!agreed) return;
    try {
      await api.del(apiPath`/devices/${fingerprint}`);
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
    }
  };

  const free = Math.max(0, answer.max - answer.devices.length);

  return (
    <>
      {dialog}
      <StatStrip
        items={[
          { label: 'Machines', value: `${answer.devices.length} / ${answer.max}` },
          { label: 'Free slots', value: String(free) },
          { label: 'Frees itself after', value: '30 days idle' },
          { label: 'Networks seen', value: String(answer.networks) }
        ]}
      />
      <div style={{ height: 20 }} />
      <Table
        columns={columns}
        header={['Machine', 'First seen', 'Last seen', '']}
        toolbar={
          <>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Installed on</div>
            <span style={{ flex: 1 }} />
            <span style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint }}>
              {free === 0 ? 'no free slot — remove one to add another machine' : `${free} free`}
            </span>
          </>
        }
      >
        {answer.devices.map((machine) => (
          <Row key={machine.fingerprint} columns={columns}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500 }}>{machine.label ?? 'Unnamed machine'}</div>
              <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>
                {machine.fingerprint.slice(0, 12)}
                {machine.country ? ` · ${machine.country}` : ''}
              </div>
            </div>
            <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkSubtle }}>{isoDay(machine.firstSeen)}</span>
            <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{isoDay(machine.lastSeen)}</span>
            <RowAction danger onClick={() => void remove(machine.fingerprint, machine.label ?? 'this computer')}>
              Remove
            </RowAction>
          </Row>
        ))}
      </Table>
      {answer.devices.length === 0 && (
        <p style={{ fontSize: 13.5, color: color.inkFaint, margin: '16px 0 0' }}>
          Nothing yet. A machine appears here the first time Airlock checks its licence on it.
        </p>
      )}
      {error && <p style={{ fontSize: 13.5, color: color.badText, margin: '16px 0 0' }}>{error}</p>}
      <div style={{ maxWidth: 700 }}>
        <Callout title="How the count works">
          Your key is the same on every machine — there is nothing to move or re-paste. A machine that has not checked
          in for thirty days releases its slot on its own, so reinstalling macOS or replacing a computer costs you
          nothing. Removing one here takes effect at that machine{'\u2019'}s next check.
          <div style={{ marginTop: 10 }}>
            We record the country and network operator a machine last checked in from — never an IP address — so you
            can tell your machines apart and we can notice a key being used in a dozen places at once. It never
            restricts anything: travelling is not abuse.
          </div>
        </Callout>
      </div>
      <DeviceTokens tokens={answer.tokens ?? []} onChanged={() => void load()} onError={setError} />
    </>
  );
}

/* ── profile ────────────────────────────────────────────────────────────── */

/** What the Worker wants before it moves an account's address. */
type Reauth = 'password' | 'code' | 'password+code' | 'signin';

function reauthOf(body: unknown): Reauth | null {
  const asked = (body as { reauth?: unknown } | null)?.reauth;
  return asked === 'password' || asked === 'code' || asked === 'password+code' || asked === 'signin' ? asked : null;
}

export function Profile({ me, onRefresh, onSignedOut }: { me: Me; onRefresh: () => Promise<void>; onSignedOut: () => void }): React.JSX.Element {
  const [name, setName] = useState(me.user.name ?? '');
  const [email, setEmail] = useState(me.user.email);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, ask] = useConfirm();
  // What the Worker asked for before it moves the address (`reauth` on its
  // 403): the current password, a second-factor code, both, or a fresh
  // sign-in. A new address is a way to take an account over — the reset link,
  // the confirmation and every mail go there — so a session alone does not
  // move it.
  const [reauth, setReauth] = useState<Reauth | null>(null);
  const [proofPassword, setProofPassword] = useState('');
  const [proofCode, setProofCode] = useState('');

  /*
   * There is no "start an organization" here any more.
   *
   * The kind is chosen at sign-up (`Auth.tsx`) and this card offered to change
   * it afterwards by creating an organization — one name field, a country
   * hard-coded to FR and a billing address taken from the sign-in email, for a
   * record that then bills a team. An account's type is not a preference on the
   * profile page, and the half of the form that was missing was the half that
   * matters. Somebody who needs one signs up for one; the route that creates
   * organizations is still there for the shell that has them (`Organizations`).
   */

  const save = async (): Promise<void> => {
    setError(null);
    setNote(null);
    const moving = email.trim().toLowerCase() !== me.user.email.toLowerCase();
    try {
      await api.patch(
        '/me',
        moving
          ? {
              name,
              email,
              ...(proofPassword ? { password: proofPassword } : {}),
              ...(proofCode.trim() ? { code: proofCode.replace(/\s+/g, '') } : {})
            }
          : { name }
      );
      setReauth(null);
      setProofPassword('');
      setProofCode('');
      await onRefresh();
      setNote(
        moving
          ? `Saved. Every other browser was signed out, ${me.user.email} was told, and a confirmation link went to ${email.trim()}.`
          : 'Saved.'
      );
    } catch (failure) {
      const asked = failure instanceof ApiError && failure.status === 403 ? reauthOf(failure.body) : null;
      if (asked) setReauth(asked);
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
    }
  };

  const changePassword = async (): Promise<void> => {
    setError(null);
    try {
      await api.post('/me/password', { current, next });
      // Every session went with it, including this one — which is the point.
      onSignedOut();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
    }
  };

  /**
   * Closing, which is not deleting (§5.25). The account stops being able to sign
   * in, its licence and its machines stop working, and the record stays: an
   * organization's history names who published what, and a trail that pointed at
   * nobody would be no trail.
   */
  const close = async (): Promise<void> => {
    const agreed = await ask({
      title: 'Close this account?',
      lede:
        'You are signed out, the licence and every linked machine stop working, and your seats are given back. The ' +
        'record of the account is kept — for the history of the organizations you worked in, and invoices for ten years.',
      confirm: 'Close the account',
      danger: true
    });
    if (!agreed) return;
    try {
      await api.patch('/me', { status: 'inactive' });
      onSignedOut();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
    }
  };

  return (
    <div className="al-two" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 20, maxWidth: 900 }}>
      {dialog}
      <Card>
        <div style={{ fontSize: 16, fontWeight: 600 }}>Profile</div>
        <div style={{ display: 'grid', gap: 14, marginTop: 20 }}>
          <Field label="Name" htmlFor="profile-name">
            <Input id="profile-name" value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label="Email" htmlFor="profile-email">
            <Input id="profile-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          </Field>
          {(reauth === 'password' || reauth === 'password+code') && (
            <Field label="Current password, to move the address" htmlFor="profile-proof-password">
              <Input
                id="profile-proof-password"
                type="password"
                autoComplete="current-password"
                value={proofPassword}
                onChange={(event) => setProofPassword(event.target.value)}
              />
            </Field>
          )}
          {(reauth === 'code' || reauth === 'password+code') && (
            <Field label="Code from your authenticator" htmlFor="profile-proof-code">
              <Input
                id="profile-proof-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={proofCode}
                onChange={(event) => setProofCode(event.target.value)}
              />
            </Field>
          )}
          {reauth === 'signin' && (
            <p style={{ fontSize: 13, color: color.inkSubtle, margin: 0 }}>
              This account has no password and no second factor to prove it is you. Sign out and back in, then change
              the address within ten minutes.
            </p>
          )}
          {/* Shown only once there is a handle to show.

              GitHub is a way in, not a connection to manage: signing in with it
              once attaches the handle to the account holding that verified
              address (`worker/github.ts`), and nothing here can attach or
              detach one. So the row is worth a line when it names how this
              account signs in — which matters to an account that has no
              password — and was worth nothing when it said GitHub was "not
              connected on this deployment yet": a row with no action, under a
              sentence that stopped being true when sign-in went live. */}
          {me.user.githubLogin && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                border: `1px solid ${color.line}`,
                borderRadius: 10,
                background: color.page
              }}
            >
              <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>GH</span>
              <div style={{ flex: 1, fontSize: 13.5 }}>
                Signs in with GitHub · <span style={{ color: color.inkFaint }}>{me.user.githubLogin}</span>
              </div>
            </div>
          )}
        </div>
        <div style={{ marginTop: 20 }}>
          <PrimaryButton onClick={() => void save()}>Save profile</PrimaryButton>
        </div>
        {note && <p style={{ fontSize: 13, color: color.accentText, margin: '12px 0 0' }}>{note}</p>}
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Card>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Password</div>
          <div style={{ display: 'grid', gap: 14, marginTop: 20 }}>
            <Field label="Current password" htmlFor="password-current">
              <Input
                id="password-current"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
                placeholder="••••••••••"
              />
            </Field>
            <Field label="New password" htmlFor="password-next">
              <Input
                id="password-next"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(event) => setNext(event.target.value)}
                placeholder="at least 12 characters"
              />
            </Field>
          </div>
          <p style={{ fontSize: 12.5, color: color.inkFaint, margin: '12px 0 0', lineHeight: 1.6 }}>
            Changing it signs out every browser, including this one.
          </p>
          <div style={{ marginTop: 18 }}>
            <GhostButton onClick={() => void changePassword()}>Change password</GhostButton>
          </div>
        </Card>

        <TwoFactorCard />

        <div style={{ background: color.surface, border: `1px solid ${color.badLine}`, borderRadius: radius.xxl, padding: 26 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: color.badText }}>Close account</div>
          <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '8px 0 16px', lineHeight: 1.6 }}>
            You can no longer sign in, and your licence and linked machines stop working immediately. The account’s record
            is kept, not deleted — the organizations you worked in keep a history of who published what — and invoices are
            kept ten years, as French accounting law requires.
          </p>
          <button
            onClick={() => void close()}
            style={{
              fontFamily: 'inherit',
              fontSize: 13.5,
              color: color.badText,
              background: color.badSoft,
              border: `1px solid ${color.badLine}`,
              padding: '11px 18px',
              borderRadius: radius.md,
              cursor: 'pointer'
            }}
          >
            Close my account
          </button>
        </div>
      </div>

      {error && <p style={{ fontSize: 13.5, color: color.badText }}>{error}</p>}
    </div>
  );
}

/** Monthly / Yearly over the offer card, the site's Pricing switch (§39.3). */
function PeriodSwitch({ value, onChange }: { value: string; onChange: (period: string) => void }): React.JSX.Element {
  const option = (id: string, label: string): React.JSX.Element => (
    <button
      type="button"
      role="radio"
      aria-checked={value === id}
      onClick={() => onChange(id)}
      style={{
        flex: 1,
        height: 30,
        border: 0,
        borderRadius: radius.chip,
        background: value === id ? color.surface : 'transparent',
        boxShadow: value === id ? '0 1px 2px rgba(0,0,0,.12)' : 'none',
        fontFamily: 'inherit',
        fontSize: 13.5,
        fontWeight: value === id ? 500 : 400,
        color: color.ink,
        cursor: 'pointer'
      }}
    >
      {label}
    </button>
  );
  return (
    <div role="radiogroup" aria-label="Billing period" style={{ display: 'flex', gap: 2, width: 300, maxWidth: '100%', padding: 3, borderRadius: radius.chip, background: color.surfaceAlt }}>
      {option('monthly', 'Monthly')}
      {option('annual', 'Yearly · save 17%')}
    </div>
  );
}
